import _ from "lodash";
import { FigureSolidDashKpiProviders } from "./categoryChartData.types";
import {
    EMPTY_ARRAY,
    SEPARATOR,
    WaterProvider,
    appMedian,
    FilterMetadataApiWithCountReturn,
    VALUE_STATUS_NAMING,
    PROVIDER_TITLE_CLASS,
    extractInitialYearWith2DimValues
} from "../misc";
import { Category, CategoryChart, deltaValue, getBaseValue, KpiReport, roundValue } from "../processors";
import { figureDeltaString } from "./figureDeltaString";
import { getConnectionSizeMedianText } from "../../utils";

const providerTitleStartEndDiv = {
    providerTitleStartSection: `<div class="${PROVIDER_TITLE_CLASS}">`,
    providerTitleEndSection: `</div>`
};

export const getPerConnectionResults = (
    kpis: string[][],
    metadata: FilterMetadataApiWithCountReturn,
    reports: KpiReport[],
    extractedProviders: WaterProvider[],
    seriesNames: string[],
    measure: string,
    category: Category,
    categoryChart: CategoryChart
): FigureSolidDashKpiProviders => {
    if (!reports || !extractedProviders || !seriesNames || !categoryChart) return null;

    const connectionSizeKPIs = kpis?.filter((__, index) => index > 0);
    const reportKPIs = kpis?.filter((__, index) => index <= 0);
    const connectionSizeKPIMap = connectionSizeKPIs?.reduce((prev, cur) => {
        prev[cur?.[0]] = 1;
        return prev;
    }, {});
    const reportKPIMap = reportKPIs?.reduce((prev, cur) => {
        prev[cur?.[0]] = 1;
        return prev;
    }, {});

    const { visHeader, visFooter, figureHeader, figureFooter, visType } = categoryChart;

    const _reports = reports.filter(d => d != null && d.year != null && d.provider != null && d.value != null);
    const connectionSizeReports = _reports.filter(d => connectionSizeKPIMap?.[d?.kpiCode] === 1);
    const yearProviderIdToConnectionSizeReports = _.groupBy(connectionSizeReports, d =>
        _.join([`${d.year}`, `${d.provider.tid}`], SEPARATOR)
    );

    reports = _reports.filter(d => reportKPIMap?.[d?.kpiCode] === 1);
    // reports = reports.filter(d => d != null && d.year != null && d.provider != null && d.value != null);

    extractedProviders = extractedProviders.filter(d => d != null);
    seriesNames = seriesNames.filter(d => d != null);

    const years = _.orderBy(_.uniq(reports?.map(d => d.year) ?? []), d => d, "asc");
    let unit = null;

    const latestYear = years[years.length - 1];

    const yearToReports = _.groupBy(reports, d => d.year);
    // const yearToConnectionSizeReports = _.groupBy(connectionSizeReports, d => d.year);

    const yearProviderIdToReports = _.groupBy(reports, d => _.join([`${d.year}`, `${d.provider.tid}`], SEPARATOR));
    // const isSewerageServices = category == null ? null : category?.isSewerageServices ? true : false;
    const linesData =
        extractedProviders?.map(provider => {
            const lineData =
                years?.map(year => {
                    const yearProviderIdKey = _.join([`${year}`, `${provider.tid}`], SEPARATOR);
                    if (!yearProviderIdToReports[yearProviderIdKey]) return null;
                    if (!yearProviderIdToConnectionSizeReports[yearProviderIdKey]) return null;

                    // const conSize =
                    //     isSewerageServices == null
                    //         ? null
                    //         : isSewerageServices
                    //         ? provider?.sewerageConnectionSize
                    //         : provider?.connectionSize;

                    const conSize = _.sum(
                        yearProviderIdToConnectionSizeReports[yearProviderIdKey].map(
                            d => getBaseValue(d.value ?? 0, d.unit)?.value
                        )
                    );

                    // if (provider.connectionSize == null || provider.connectionSize === 0) return null;
                    if (conSize == null || conSize === 0) return null;

                    const _reports = yearProviderIdToReports[yearProviderIdKey] ?? EMPTY_ARRAY;
                    const _values = _reports.map(d => getBaseValue(d.value ?? 0, d.unit));

                    const baseUnits = _.uniq(_values.map(v => v.unit));

                    if (baseUnits.length !== 1) return 0;

                    const baseUnit = baseUnits?.[0];

                    if (unit != null && baseUnit !== unit) return 0;

                    unit = baseUnit;

                    const sum = _.sumBy(_values, d => d.value ?? 0);

                    return roundValue(sum / conSize, unit, true, false).value;
                }) ?? [];

            return lineData;
        }) ?? [];

    const providersLatestYearData =
        extractedProviders?.map(provider => {
            const yearProviderIdKey = _.join([`${latestYear}`, `${provider.tid}`], SEPARATOR);
            if (!yearProviderIdToReports[yearProviderIdKey]) return null;
            return yearProviderIdToReports[yearProviderIdKey];
        }) ?? [];

    const connectionSizeCategories = _.uniq(
        extractedProviders?.map(d => d.connectionSizeCategory)?.filter(d => d != null)
    );

    const providerMedianSeriesNames = connectionSizeCategories.map(connectionSizeCategory =>
        getConnectionSizeMedianText(metadata, connectionSizeCategory)
    );

    // const largeProviders = _.uniqBy(
    //     reports?.filter(d => d?.provider?.connectionSizeCategory === "large")?.map(d => d.provider) ?? [],
    //     d => d.tid
    // );

    // const totalConnectionSizes = connectionSizeCategories.map(connectionSizeCategory=>{
    //     reports?.filter(d => d?.provider?.connectionSizeCategory === connectionSizeCategory)?.map(d => d.provider) ?? [],
    // });

    // _.sum(
    //     largeProviders?.map(d =>
    //         isSewerageServices == null
    //             ? 0
    //             : isSewerageServices
    //             ? d?.sewerageConnectionSize ?? 0
    //             : d?.connectionSize ?? 0
    //     ) ?? []
    // );

    const average = connectionSizeCategories.map(connectionSizeCategory => {
        return (
            years.map(year => {
                // if (totalConnectionSize === 0) return 0;

                const results = yearToReports[year] ?? ([] as KpiReport[]);

                const conSizeCategoryResults = results.filter(d => {
                    const yearProviderIdKey = _.join([`${year}`, `${d?.provider?.tid}`], SEPARATOR);

                    return (
                        d?.provider?.connectionSizeCategory === connectionSizeCategory &&
                        // connection size properties are provided
                        yearProviderIdToConnectionSizeReports[yearProviderIdKey]?.filter(d => d.value != null)?.length >
                            0 &&
                        // isSewerageServices != null &&
                        // (isSewerageServices ? d?.provider?.sewerageConnectionSize : d?.provider?.connectionSize) !=
                        //     null &&

                        d.value != null
                    );
                });

                // con size sum
                // const totalConnectionSize = _.sum(large.map(d => d?.provider?.connectionSize ?? 0));

                // value sum
                let _values = (
                    conSizeCategoryResults?.map(d => {
                        const yearProviderIdKey = _.join([`${year}`, `${d?.provider?.tid}`], SEPARATOR);
                        const connectionSize = _.sum(
                            yearProviderIdToConnectionSizeReports[yearProviderIdKey]
                                ?.filter(d => d.value != null)
                                ?.map(d => getBaseValue(d.value ?? 0, d.unit)?.value)
                        );

                        return {
                            value: getBaseValue(d.value ?? 0, d.unit),

                            connectionSize
                            // isSewerageServices == null
                            //     ? null
                            //     : isSewerageServices
                            //     ? d?.provider?.sewerageConnectionSize
                            //     : d?.provider?.connectionSize
                        };
                    }) ?? []
                ).filter(d => d?.connectionSize != null);

                // const totalConnectionSize = _.sum(_values?.map(v => v.connectionSize ?? 0));
                // if (totalConnectionSize === 0) return 0;

                const baseUnits = _.uniq(_values?.map(v => v?.value?.unit) ?? []);

                if (baseUnits.length !== 1) return 0;

                const baseUnit = baseUnits?.[0];

                if (unit != null && baseUnit !== unit) return 0;

                if (unit == null) {
                    unit = baseUnit;
                }

                const avg = roundValue(
                    appMedian(_values?.map(d => (d?.value?.value ?? 0) / d.connectionSize) ?? []),
                    unit,
                    true,
                    false
                )?.value;

                return avg;

                // return roundValue(avg / totalConnectionSize, unit).value;
            }) ?? []
        );
    });

    // const largeProviderAverageSeriesName = "Large provider median";
    const linesDataSeriesName = extractedProviders?.map((_, index) => `${seriesNames?.[index]}`);
    // const linesDataSeriesName = extractedProviders;

    const startYear = extractInitialYearWith2DimValues(years, linesData, average);

    const visHeaderProcessed = _.template(visHeader?.value ?? "")({
        startYear,
        endYear: latestYear
    });

    const visFooterProcessed = _.template(visFooter?.value ?? "")({
        startYear,
        endYear: latestYear
    });

    const graph = {
        measure,
        header:
            !visHeader || visHeader.processed.trim() === ""
                ? null
                : {
                      processed: visHeaderProcessed,
                      value: visHeaderProcessed,
                      format: visHeaderProcessed
                  },
        footer:
            !visFooter || visFooter.processed.trim() === ""
                ? null
                : {
                      processed: visFooterProcessed,
                      value: visFooterProcessed,
                      format: visFooterProcessed
                  },

        years,

        providerMedianSeriesNames,
        providerMedianAnnotations: providerMedianSeriesNames,
        providerMedianData: average,

        // largeProviderAverageSeriesName,
        // largeProviderAverageData,
        linesData,
        linesDataSeriesName
    };

    const figures = extractedProviders?.map((provider, providerIndex) => {
        const providerLinesData = linesData[providerIndex] ?? [];

        const latestYearValue = providerLinesData[providerLinesData?.length - 1];
        const previousYearValue = providerLinesData[providerLinesData?.length - 2];
        const latestYearReports = providersLatestYearData[providerIndex];
        const valueStatus = latestYearReports?.[0]?.valueStatus;

        // if data does not exist, return NA/ND view
        if (latestYearValue == null) {
            const figureHeaderProcessed = _.template(figureHeader?.value ?? "")({
                ...providerTitleStartEndDiv,
                year: latestYear,
                value: null,
                deltaString: null,
                providerName: provider.name
            });

            const figureFooterProcessed = _.template(figureFooter?.value ?? "")({
                ...providerTitleStartEndDiv,
                year: latestYear,
                value: null,
                deltaString: null,
                providerName: provider.name
            });

            const yearProviderIdKey = _.join([`${latestYear}`, `${provider?.tid}`], SEPARATOR);
            const consizeValues = yearProviderIdToConnectionSizeReports[yearProviderIdKey]
                ?.filter(d => d.value != null)
                ?.map(d => getBaseValue(d.value ?? 0, d.unit)?.value);

            const conSize = consizeValues?.length > 0 ? _.sum(consizeValues) : null;

            // const conSize =
            //     isSewerageServices == null
            //         ? null
            //         : isSewerageServices
            //         ? provider?.sewerageConnectionSize
            //         : provider?.connectionSize;

            return {
                header:
                    !figureHeader || figureHeader.processed.trim() === ""
                        ? null
                        : {
                              processed: figureHeaderProcessed,
                              value: figureHeaderProcessed,
                              format: figureHeaderProcessed
                          },
                footer:
                    !figureFooter || figureFooter.processed.trim() === ""
                        ? null
                        : {
                              processed: figureFooterProcessed,
                              value: figureFooterProcessed,
                              format: figureFooterProcessed
                          },
                value:
                    conSize == null
                        ? VALUE_STATUS_NAMING.NR
                        : valueStatus != null
                        ? VALUE_STATUS_NAMING?.[valueStatus] ?? valueStatus
                        : previousYearValue != null && latestYearValue == null
                        ? VALUE_STATUS_NAMING.MD
                        : VALUE_STATUS_NAMING.NR,
                decimals: 0
            };
        }

        const delta = deltaValue(previousYearValue, latestYearValue, unit);

        const deltaString = figureDeltaString(delta, category, providerIndex);
        const figureHeaderProcessed = _.template(figureHeader?.value ?? "")({
            ...providerTitleStartEndDiv,
            year: `${latestYear}`,
            value: latestYearValue,
            deltaString,
            providerName: provider.name
        });

        const figureFooterProcessed = _.template(figureFooter?.value ?? "")({
            ...providerTitleStartEndDiv,
            year: `${latestYear}`,
            value: latestYearValue,
            deltaString,
            providerName: provider.name
        });

        return {
            header:
                !figureHeader || figureHeader.processed.trim() === ""
                    ? null
                    : {
                          processed: figureHeaderProcessed,
                          value: figureHeaderProcessed,
                          format: figureHeaderProcessed
                      },
            footer:
                !figureFooter || figureFooter.processed.trim() === ""
                    ? null
                    : {
                          processed: figureFooterProcessed,
                          value: figureFooterProcessed,
                          format: figureFooterProcessed
                      },
            value: latestYearValue,
            decimals: roundValue(latestYearValue, unit, true, false).decimals
        };
    });

    return {
        waterProviders: extractedProviders,
        unit,
        year: latestYear,
        figures: !(visType === "figure" || visType === "figureWithSolidDashLine") ? null : figures,
        graph: visType === "figure" ? null : graph
    };
};
